import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockPartners } from '@/mocks/partners';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockLeads } from '@/mocks/leads';
import { mockOffers } from '@/mocks/offers';
import BaseSelect from '@/components/base/BaseSelect';
import { ArrowLeft, ChevronRight, FileText, Calculator, CheckCircle } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const taxTreatments = [
  { value: 'vat_standard', label: 'VAT Standard (21%)', rate: 21 },
  { value: 'vat_reverse_charge', label: 'VAT Reverse Charge (0%)', rate: 0 },
  { value: 'exempt', label: 'Exempt (0%)', rate: 0 },
  { value: 'us_no_sales_tax', label: 'US — No Sales Tax (0%)', rate: 0 },
];

export default function InvoiceNewPage() {
  const navigate = useNavigate();
  const [partnerId, setPartnerId] = useState('');
  const [periodStart, setPeriodStart] = useState(format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [periodEnd, setPeriodEnd] = useState(format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [taxTreatment, setTaxTreatment] = useState('vat_standard');
  const [showPreview, setShowPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  const partner = mockPartners.find(p => p.id === partnerId);
  const taxRate = taxTreatments.find(t => t.value === taxTreatment)?.rate || 0;

  const previewLines = useMemo(() => {
    if (!partnerId) return [];
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    end.setHours(23, 59, 59, 999);

    return mockPayoutLines
      .filter(p => {
        const d = new Date(p.created_at);
        return p.partner_id === partnerId && p.status === 'approved' && d >= start && d <= end;
      })
      .map(p => {
        const lead = mockLeads.find(l => l.id === p.lead_id);
        const offer = mockOffers.find(o => o.id === p.offer_id);
        return {
          id: p.id,
          description: `${offer?.payout_model.toUpperCase() || 'Payout'} — ${lead?.firstname || 'Lead'} ${lead?.lastname || ''}`.trim(),
          amount_local: p.amount_local,
          amount_eur: p.amount_eur,
          currency: p.currency,
        };
      });
  }, [partnerId, periodStart, periodEnd]);

  const subtotal = previewLines.reduce((s, l) => s + l.amount_eur, 0);
  const taxAmount = Math.round(subtotal * (taxRate / 100) * 100) / 100;
  const total = subtotal + taxAmount;

  const generateInvoice = useCallback(async () => {
    if (!partnerId || previewLines.length === 0) return;
    setGenerating(true);
    await new Promise(r => setTimeout(r, 900)); // simulate async
    setGenerating(false);
    setGenerated(true);
    setTimeout(() => navigate('/finance/invoices'), 1200);
  }, [partnerId, previewLines, navigate]);

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/finance/invoices')}
          className="p-1.5 rounded-lg transition-colors hover-surface"
          style={{ color: 'var(--text-muted)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Generate Invoice</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Select partner and period</p>
        </div>
      </div>

      {/* Form */}
      <div className="rounded-xl p-5 space-y-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Partner</label>
            <BaseSelect
              value={partnerId}
              onChange={val => { setPartnerId(val); setShowPreview(false); }}
              options={[{ value: '', label: 'Select a partner...' }, ...mockPartners.map(p => ({ value: p.id, label: `${p.display_name} (${p.country})` }))]}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Tax Treatment</label>
            <BaseSelect
              value={taxTreatment}
              onChange={val => setTaxTreatment(val)}
              options={taxTreatments.map(t => ({ value: t.value, label: t.label }))}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Period Start</label>
            <input
              type="date"
              value={periodStart}
              onChange={e => { setPeriodStart(e.target.value); setShowPreview(false); }}
              className="w-full px-3 py-2 text-sm rounded-lg transition-all focus:outline-none focus:ring-1"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Period End</label>
            <input
              type="date"
              value={periodEnd}
              onChange={e => { setPeriodEnd(e.target.value); setShowPreview(false); }}
              className="w-full px-3 py-2 text-sm rounded-lg transition-all focus:outline-none focus:ring-1"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
            />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() => setShowPreview(true)}
            disabled={!partnerId}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
          >
            <Calculator size={14} />
            Preview
          </button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-separator)' }}>
            <div className="flex items-center gap-2">
              <FileText size={16} style={{ color: 'var(--text-muted)' }} />
              <h2 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Invoice Preview</h2>
            </div>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {partner?.display_name} · {periodStart} to {periodEnd}
            </span>
          </div>

          <div className="overflow-x-auto table-scroll">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>#</th>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Description</th>
                  <th className="text-right px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Amount (EUR)</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
                {previewLines.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      No approved payout lines found for this partner and period.
                    </td>
                  </tr>
                )}
                {previewLines.map((line, i) => (
                  <tr key={line.id} className="transition-colors hover-surface">
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{i + 1}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-tertiary)' }}>{line.description}</td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>€{line.amount_eur.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-4 border-t" style={{ borderColor: 'var(--border-separator)' }}>
            <div className="flex flex-col items-end gap-1 text-sm">
              <div className="flex items-center gap-4">
                <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
                <span className="font-medium w-24 text-right" style={{ color: 'var(--text-secondary)' }}>€{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-4">
                <span style={{ color: 'var(--text-muted)' }}>Tax ({taxRate}%)</span>
                <span className="font-medium w-24 text-right" style={{ color: 'var(--text-secondary)' }}>€{taxAmount.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-4 pt-1 border-t mt-1" style={{ borderColor: 'var(--border-separator)' }}>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>Total</span>
                <span className="font-bold w-24 text-right text-base" style={{ color: 'var(--text-primary)' }}>€{total.toLocaleString()}</span>
              </div>
            </div>
            <div className="mt-2 text-right">
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {taxTreatments.find(t => t.value === taxTreatment)?.label}
              </span>
            </div>
          </div>

          <div className="px-5 py-3 border-t flex items-center justify-end gap-2" style={{ borderColor: 'var(--border-separator)', background: 'var(--bg-surface-raised)' }}>
            <button
              onClick={() => setShowPreview(false)}
              className="px-3 py-1.5 text-xs rounded-lg transition-colors hover-surface"
              style={{ color: 'var(--text-muted)' }}
            >
              Back
            </button>
            <button
              onClick={generateInvoice}
              disabled={!partnerId || previewLines.length === 0 || generating || generated}
              className="flex items-center gap-2 px-5 py-2.5 text-sm rounded-xl transition-all font-medium"
              style={{
                background: generated ? 'var(--success-bg)' : generating ? 'var(--bg-input)' : 'var(--accent)',
                color: generated ? 'var(--success-text)' : generating ? 'var(--text-muted)' : 'var(--text-inverse)',
                opacity: (!partnerId || previewLines.length === 0) ? 0.4 : 1,
              }}
            >
              {generated ? (
                <><CheckCircle size={15} /> Invoice created — redirecting...</>
              ) : generating ? (
                <><div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border-separator)', borderTopColor: 'var(--text-secondary)' }} /> Generating...</>
              ) : (
                <><FileText size={15} /> Generate Invoice</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
